import { PlusIcon, XIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";

// Shared layout primitives for Atlas.
//
// Type scale (deliberate, top-down):
//   Page title      text-2xl  font-gilroy semibold   (in Atlas.tsx)
//   Section title   text-lg   font-gilroy semibold, with a gray-300 index
//   Question label  text-sm   font-gilroy semibold gray-900
//   Guidance        text-sm   gray-400
//   Input text      text-sm   (passed via className so placeholders never
//                              outweigh the headers above them)

// A section group — flows inside the page canvas (no nested card), separated
// by generous spacing and a rule line under its header.
export function AtlasSection(props: { id: string, index: number, title: string, description: string, children: React.ReactNode }) {
  return (
    <div id={props.id} className="flex flex-col pt-12 first:pt-8 scroll-mt-4">
      <div className="flex flex-col pb-4 border-b border-gray-200">
        <div className="flex items-baseline gap-3">
          <div className="font-gilroy font-semibold text-lg text-gray-300 tabular-nums">
            {String(props.index).padStart(2, "0")}
          </div>
          <div className="font-gilroy font-semibold text-lg text-gray-900">{props.title}</div>
        </div>
        <div className="text-sm text-gray-400 font-semibold mt-1 max-w-3xl">{props.description}</div>
      </div>
      {props.children}
    </div>
  );
}

// The core layout unit: question + guidance on the left, answer on the right.
// Collapses to stacked on narrow widths.
export function AtlasFieldRow(props: { label: string, description?: string, children: React.ReactNode }) {
  return (
    <div className="flex flex-col lg:flex-row gap-3 lg:gap-12 py-7 border-b border-gray-100 last:border-b-0">
      <div className="flex flex-col lg:w-2/5 flex-shrink-0">
        <div className="font-gilroy font-semibold text-sm text-gray-900 leading-snug">{props.label}</div>
        {props.description &&
          <div className="text-sm text-gray-400 mt-1.5 pr-4 leading-snug">{props.description}</div>
        }
      </div>
      <div className="flex flex-col lg:w-3/5 min-w-0">
        {props.children}
      </div>
    </div>
  );
}

// Generic add/remove list for the right-hand column — same interaction as the
// email-alias list in SettingsProfile.
export function AtlasList<T>(props: {
  items: T[],
  onChange: (items: T[]) => void,
  blankItem: T,
  addLabel: string,
  renderItem: (item: T, update: (item: T) => void, index: number) => React.ReactNode,
  itemClassName?: string,
}) {
  const { t } = useTranslation();

  function updateItem(index: number, item: T) {
    const items = [...props.items];
    items[index] = item;
    props.onChange(items);
  }

  function removeItem(index: number) {
    props.onChange(props.items.filter((_, i) => i !== index));
  }

  function addItem() {
    props.onChange([...props.items, props.blankItem]);
  }

  return (
    <div className="flex flex-col gap-3">
      {props.items.map((item, index) => (
        <div key={index} className={`flex items-start gap-2 ${props.itemClassName ?? ""}`}>
          <div className="flex-grow min-w-0">
            {props.renderItem(item, (updated) => updateItem(index, updated), index)}
          </div>
          <button
            className="flex-shrink-0 mt-2 p-1 rounded text-gray-400 hover:text-red hover:bg-gray-50"
            onClick={() => removeItem(index)}
            aria-label={t("Remove item")}
            title={t("Remove")}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button className="btn btn-white text-sm flex items-center gap-1 self-start" onClick={addItem}>
        <PlusIcon className="w-4 h-4" />
        {props.addLabel}
      </button>
    </div>
  );
}
